import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { AuthStore } from "stores/AuthStore";
import { generateSignalROptions } from "stores/RealTimeMessages/TaskStore";
import RootStore from "stores/RootStore";
import BaseService from "./BaseService";

export type SyntheticDataBatchRequest = {
    batchId: string;
    questionId: string;
    labelName: string;
    labelValue: string;
    labelRubric: string;
    labelRubricVersion: string;
    labelTaxonomy: { value: string; definition: string }[];
    count: number;
    maxUpsamplingRatio: number;
    deliberateHardExamples: boolean;
    instructions: string | null;
    sourceAudioMetadataIds: string[];
};

export type SyntheticDataBatchStatus =
    | "Queued"
    | "Running"
    | "Succeeded"
    | "Failed";

/** One batch as the worker last saved it. Counts update while it is Running. */
export type SyntheticDataBatch = {
    batchId: string;
    classifierId: string | null;
    // The rubric's question. Null for batches from before rubrics.
    questionId: string | null;
    status: SyntheticDataBatchStatus;
    statusReason: string | null;
    labelName: string;
    labelValue: string;
    requestedCount: number;
    generatedCount: number;
    rejectedCount: number;
    startedOn: string | null;
    completedOn: string | null;
    createdOn: string;
};

class SyntheticDataService extends BaseService {
    protected urlBase: string = "api/SyntheticDataOrchestrator";

    /** Returns the model's batches, newest first. */
    public getBatches(classifierId: string): Promise<SyntheticDataBatch[]> {
        return this.get<SyntheticDataBatch[]>(
            new URLSearchParams({ classifierId }),
            "Batches",
        );
    }

    /** Marks the model's batches inactive so a new set can replace them. */
    public async clearBatches(classifierId: string): Promise<void> {
        await this.delete(new URLSearchParams({ classifierId }), "Batches");
    }

    /**
     * Opens a live connection for one batch's progress. Call `subscribe` to start it and again after
     * any reconnect, since a new connection isn't subscribed.
     */
    public connectToBatch(batchId: string) {
        const authStore = RootStore().getStore(AuthStore);
        // Keep the batch's organization even if the user switches organizations while it generates.
        const organizationId =
            authStore.getRequestHeader()["Acx-OrganizationId"];
        const connection = new HubConnectionBuilder()
            .withUrl(
                `/hubs/synthetic-data?ws_organizationId=${encodeURIComponent(organizationId)}`,
                generateSignalROptions(authStore),
            )
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (context) =>
                    Math.min(1000 * (context.previousRetryCount + 1), 30_000),
            })
            .build();
        const subscribe = async () => {
            if (connection.state === HubConnectionState.Disconnected)
                await connection.start();
            if (connection.state === HubConnectionState.Connected)
                await connection.invoke("Subscribe", organizationId, batchId);
        };
        return { connection, subscribe };
    }

    /**
     * Starts one synthetic data batch. Sending the same `batchId` again does not start a second batch.
     */
    public requestBatch(
        request: SyntheticDataBatchRequest,
        classifierId: string,
        rubricId: string,
        tagId?: string,
    ): Promise<{ batchId: string }> {
        const params = new URLSearchParams({ classifierId, rubricId });
        if (tagId) params.set("tagId", tagId);
        return this.post<{ batchId: string }>(
            JSON.stringify(request),
            params,
            "Batches",
            undefined,
            // Requests to one URL are debounced together, so each batch needs its own key.
            request.batchId,
        );
    }
}

export default SyntheticDataService;
